/**
 * Interactive "custom" workflow scripts.
 *
 * Each entry defines a scenario as an ordered list of real-app screens the
 * promoter (or public visitor) walks through.  The screens are rendered by
 * ./CustomWorkflow.jsx and are pixel-close to the real ConfirmPH pages.
 *
 * Contract:
 *   {
 *     id, kind: "custom", business,
 *     defaultDevice?: "desktop"|"phone",
 *     supportsDeviceToggle?: false,        // we render desktop-only
 *     steps: [
 *       {
 *         screen: string,     // see ScreenSwitch map in CustomWorkflow
 *         sidebarActive: string,
 *         title?: string,
 *         hint?: string,
 *         data: object,
 *         actionLabel?: string,
 *         outcome?: string,   // key into `summaries` when this step ends
 *       }, ...
 *     ],
 *     summaries: { [outcome]: { title, what, related?[] } }
 *   }
 *
 * NOTE: Everything here is fictional data: no bookings, users or plates
 * refer to anything real.
 */

import { INCIDENT_CHAT, INCIDENT_SCENARIO } from "../../../../components/walkthrough2/incidentScenario";

const damagePhoto = "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/a59gh4xo_damaged_car.png";

// -----------------------------------------------------------------
// BOOKING PROBLEMS
// -----------------------------------------------------------------

const awaitingConfirmation = {
  id: "awaiting-confirmation",
  kind: "custom",
  business: "Bacolod Ride Hub",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "reminders-list",
      sidebarActive: "reminders",
      title: "Follow-ups screen",
      hint: "This is your Follow-ups page: ConfirmPH surfaces stale confirmations for you.",
      data: {
        focusId: "r1",
        items: [
          { id: "r1", priority: "high", status: "pending", type: "Booking · pending confirmation",
            title: "Ruth Cabales hasn't confirmed yet",
            message: "Confirmation link sent 6h ago. Follow up before pickup on Saturday.",
            to: "ruth@example.com", due: "Today · 17:00" },
          { id: "r2", priority: "medium", status: "pending", type: "Booking · pre-pickup",
            title: "Pre-pickup check: David Cruz",
            message: "Booking picks up in 24h. Confirm plate + fuel status.",
            to: "david@example.com", due: "Tomorrow · 09:00" },
          { id: "r3", priority: "low", status: "sent", type: "Return follow-up",
            title: "Return prep: Anna Reyes",
            message: "Booking returns tomorrow. Ready return-inspection checklist.",
            to: "anna@example.com", due: "Tomorrow · 10:00" },
        ],
      },
      actionLabel: "Send Ruth's follow-up now",
    },
    {
      screen: "booking-detail",
      sidebarActive: "booking-detail",
      title: "Open the booking",
      hint: "One tap on the follow-up jumps straight into the linked booking.",
      data: {
        booking: {
          id: "R-4471", customer: "Ruth Cabales", vehicle: "Honda BR-V · KCM-6641",
          pickup: "Sat · 09:00", ret: "Sun · 18:00",
          source: "Messenger", status: "Pending customer confirmation",
          subtotal: "₱4,800", deposit: "₱1,500", balance: "₱3,300",
        },
        banners: [
          { tone: "warn", title: "Awaiting customer confirmation",
            text: "Confirmation link was generated 6h ago. Send another reminder or resend the link." },
        ],
        primaryAction: "Resend confirmation link",
      },
    },
    {
      screen: "confirmation-preview",
      sidebarActive: "booking-detail",
      title: "Fresh confirmation link ready",
      data: {
        id: "R4471", customer: "Ruth Cabales", vehicle: "Honda BR-V · KCM-6641",
        pickup: "Sat · 09:00", ret: "Sun · 18:00",
      },
      actionLabel: "Copy & send to Ruth",
    },
    {
      screen: "banner-info",
      sidebarActive: "booking-detail",
      hint: "Ruth taps the link on her phone and confirms.",
      data: {
        tone: "ok",
        title: "Ruth confirmed the booking",
        body: "Booking status auto-updates to 'Customer Confirmed'. Nothing to chase.",
      },
      outcome: "confirmed",
    },
  ],
  summaries: {
    confirmed: {
      title: "Follow-up closed the loop",
      what: "The Follow-ups page surfaced Ruth's stale confirmation, one tap resent the link, and Ruth confirmed. Nothing lost to a forgotten Messenger thread.",
      related: ["messenger-inquiry", "follow-up-reminder", "upcoming-pickup"],
    },
  },
};

const forgottenBooking = {
  id: "forgotten-messenger-booking",
  kind: "custom",
  business: "Dumaguete Island Wheels",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "banner-info",
      sidebarActive: "dashboard",
      title: "Customer arrives at the counter",
      hint: "Ella insists she has a booking. You need to check ConfirmPH first.",
      data: {
        tone: "warn",
        title: "Ella Torres claims a Vios reservation for today 10 AM",
        body: "\"Sir, reserved Vios na po ako with Kuya Jay last week. Confirmed po daw.\"",
        primary: "Search ConfirmPH for Ella's booking",
      },
    },
    {
      screen: "find-booking",
      sidebarActive: "find-booking",
      title: "Find booking",
      hint: "Search by name, phone, plate or booking ID.",
      data: { prefillQuery: "Ella Torres" },
      actionLabel: "Recover into Notepad now",
    },
    {
      screen: "notepad-recover",
      sidebarActive: "notepad",
      title: "Recover the booking into the Notepad",
      hint: "Better late than lost: record it before the shift changes again.",
      data: {
        suggested: "Ella Torres, 0917-000-4413. Toyota Vios. Today · 10:00 pickup at shop. Verbally reserved via Messenger last week (staff: Kuya Jay). Recovering into ConfirmPH now.",
      },
      actionLabel: "Convert to Booking Draft",
    },
    {
      screen: "banner-info",
      sidebarActive: "dashboard",
      data: {
        tone: "ok",
        title: "Booking recovered: now visible in ConfirmPH",
        body: "The record exists. Future staff can see it, and ConfirmPH can now warn if it conflicts with another booking.",
      },
      outcome: "recovered",
    },
  ],
  summaries: {
    recovered: {
      title: "Verbal booking captured: no more \"invisible\" bookings",
      what: "The Messenger conversation that was never entered is now a real booking record. This is exactly why every intake should live in ConfirmPH: the app can only warn about bookings it can see.",
      related: ["messenger-inquiry", "walk-in", "awaiting-confirmation"],
    },
  },
};

const dateChange = {
  id: "customer-date-change",
  kind: "custom",
  business: "Iloilo Drive PH",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "reminders-list",
      sidebarActive: "reminders",
      title: "Customer requested a change",
      hint: "The change request appears in Follow-ups (customer replied through the confirmation link).",
      data: {
        focusId: "c1",
        items: [
          { id: "c1", priority: "high", status: "pending", type: "Booking · change requested",
            title: "Grace Lim wants to move return to Jun 16",
            message: "Original: Jun 12 → Jun 14. Requested: Jun 12 → Jun 16. Same vehicle, same pickup.",
            to: "grace@example.com", due: "Today · 12:00" },
        ],
      },
      actionLabel: "Open Grace's booking",
    },
    {
      screen: "booking-detail",
      sidebarActive: "booking-detail",
      title: "Booking · Grace Lim",
      data: {
        booking: {
          id: "G-2298", customer: "Grace Lim", vehicle: "Toyota Wigo · TDN-2298",
          pickup: "Jun 12 · 09:00", ret: "Jun 14 · 09:00",
          source: "Messenger", status: "Change requested",
        },
        banners: [
          { tone: "warn", title: "Customer requested a change",
            text: "Update the return date, re-check for conflicts, then resend the confirmation link." },
        ],
        primaryAction: "Edit dates",
      },
    },
    {
      screen: "edit-booking-dates",
      sidebarActive: "edit-booking",
      title: "Edit booking dates",
      hint: "Change the return date. Save will re-run the conflict check.",
      data: {
        customer: "Grace Lim", vehicle: "Toyota Wigo · TDN-2298",
        pickup: "Jun 12 · 09:00", ret: "Jun 16 · 09:00",
      },
      actionLabel: "Save & re-check",
    },
    {
      screen: "banner-info",
      sidebarActive: "booking-detail",
      data: {
        tone: "ok",
        title: "No conflicts on Toyota Wigo · Jun 14–16",
        body: "Dates updated. Confirmation link auto-refreshed for Grace.",
        primary: "Resend fresh confirmation link",
      },
    },
    {
      screen: "confirmation-preview",
      sidebarActive: "booking-detail",
      title: "Fresh confirmation link generated",
      data: { id: "G2298", customer: "Grace Lim", vehicle: "Toyota Wigo · TDN-2298",
              pickup: "Jun 12 · 09:00", ret: "Jun 16 · 09:00" },
      actionLabel: "Copy & send to Grace",
      outcome: "date_changed",
    },
  ],
  summaries: {
    date_changed: {
      title: "Booking rescheduled without touching Messenger",
      what: "The edit updated the booking record and produced a fresh confirmation link: no manual re-typing, no chat screenshots.",
      related: ["double-booking", "awaiting-confirmation", "messenger-inquiry"],
    },
  },
};

// -----------------------------------------------------------------
// VEHICLE PROBLEMS
// -----------------------------------------------------------------

const maintenance = {
  id: "vehicle-maintenance",
  kind: "custom",
  business: "Cebu Wheels & Wander",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "vehicles-list",
      sidebarActive: "vehicles",
      title: "Vehicles page",
      hint: "Open the Almera to schedule its service window.",
      data: {
        focusId: "v1",
        vehicles: [
          { id: "v1", name: "Nissan Almera", plate: "AAB-2214", status: "available",
            category: "Sedan", transmission: "Automatic", seats: 5, price: 1900 },
          { id: "v2", name: "Toyota Vios", plate: "WSJ-2211", status: "available",
            category: "Sedan", transmission: "Automatic", seats: 5, price: 1700 },
          { id: "v3", name: "Toyota Innova", plate: "LFR-4098", status: "booked",
            category: "Van", transmission: "Automatic", seats: 7, price: 2800 },
        ],
      },
    },
    {
      screen: "vehicle-detail",
      sidebarActive: "vehicle-detail",
      title: "Nissan Almera: vehicle detail",
      data: {
        name: "Nissan Almera", plate: "AAB-2214", status: "available",
        next: "Karen Ong · Jul 21 · 09:00",
        primary: "Schedule maintenance window",
      },
      actionLabel: "Schedule maintenance",
    },
    {
      screen: "maintenance-form",
      sidebarActive: "maintenance-form",
      title: "Maintenance form",
      hint: "Enter the service window: overlapping bookings will be blocked.",
      data: { vehicle: "Nissan Almera", plate: "AAB-2214",
              start: "Jul 20 · 08:00", end: "Jul 22 · 18:00",
              reason: "Full service + brake pads" },
      actionLabel: "Save window",
    },
    {
      screen: "warnings-list",
      sidebarActive: "warnings",
      title: "Warning Center picks up a conflict",
      hint: "An earlier draft for Karen Ong overlapped this window: ConfirmPH flags it automatically.",
      data: {
        focusId: "w1",
        items: [
          { id: "w1", severity: "high", type: "Vehicle unavailable", status: "active",
            title: "Karen Ong · Jul 21 draft overlaps maintenance",
            message: "Nissan Almera is scheduled for maintenance Jul 20 – Jul 22.",
            recommended: "Assign a different vehicle, or move Karen's dates.",
            primary: "Reassign vehicle" },
        ],
      },
      actionLabel: "Reassign Karen's draft to another vehicle",
    },
    {
      screen: "banner-info",
      sidebarActive: "warnings",
      data: {
        tone: "ok",
        title: "Draft reassigned to Toyota Vios · WSJ-2211",
        body: "Warning auto-resolved. The scheduled maintenance is safe.",
      },
      outcome: "maintenance_blocked",
    },
  ],
  summaries: {
    maintenance_blocked: {
      title: "Scheduled maintenance protected the fleet",
      what: "The Almera was locked out during its service window. The Warning Center automatically caught the overlapping draft and offered a replacement: nothing slipped through.",
      related: ["vehicle-breakdown", "damage-incident", "replacement-vehicle"],
    },
  },
};

const damageIncident = {
  id: "damage-incident",
  kind: "custom",
  business: "Dumaguete Island Wheels",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "booking-detail",
      sidebarActive: "booking-detail",
      title: "Return inspection on Ford EcoSport",
      data: {
        booking: {
          id: "F-1102", customer: "Anna Reyes", vehicle: "Ford EcoSport · ZZM-1102",
          pickup: "Fri · 09:00", ret: "Today · 10:00 (returned)",
          source: "Messenger", status: "Returned: inspection due",
        },
        banners: [
          { tone: "warn", title: "Return inspection required",
            text: "Vehicle back in the yard. Photograph any new damage before releasing the deposit." },
        ],
        primaryAction: "Log incident with photos",
      },
    },
    {
      screen: "damage-incident",
      sidebarActive: "damage-incident",
      title: "Log incident",
      hint: "Photos + notes stay tied to this booking and this vehicle.",
      data: {
        vehicle: "Ford EcoSport", plate: "ZZM-1102",
        title: "Front-left impact: return inspection",
        severity: "Moderate",
        photo: damagePhoto,
        notes: "Front bumper cracked, headlight housing loose. Renter admits parking-lot impact; will cover repair.",
        affected: [
          { customer: "Maria Santos", when: "Fri · 10:00 (upcoming)" },
        ],
      },
      actionLabel: "Save incident",
    },
    {
      screen: "warnings-list",
      sidebarActive: "warnings",
      title: "Future bookings on the same vehicle surfaced",
      hint: "ConfirmPH links the incident to upcoming bookings that depend on this vehicle.",
      data: {
        focusId: "w1",
        items: [
          { id: "w1", severity: "high", type: "Vehicle issue", status: "active",
            title: "Maria Santos · Fri 10:00 depends on Ford EcoSport",
            message: "Vehicle in repair after front-left impact. Booking is at risk.",
            recommended: "Assign a replacement or contact Sam about a delay.",
            primary: "Reassign vehicle" },
        ],
      },
      actionLabel: "Assign a replacement",
    },
    {
      screen: "banner-info",
      sidebarActive: "warnings",
      data: {
        tone: "ok",
        title: "Sam moved to Toyota Rush · TRX-8871",
        body: "Booking, deposit and dates preserved on the same record. Sam gets a fresh confirmation link.",
      },
      outcome: "incident_logged",
    },
  ],
  summaries: {
    incident_logged: {
      title: "Damage recorded: future bookings protected",
      what: "Photos stay tied to the booking and vehicle. Future bookings that depended on the damaged EcoSport were surfaced automatically so the customer never found out the hard way.",
      related: ["vehicle-breakdown", "replacement-vehicle", "maintenance"],
    },
  },
};

// -----------------------------------------------------------------
// STAFF & OPERATIONS
// -----------------------------------------------------------------

const followUp = {
  id: "follow-up-reminder",
  kind: "custom",
  business: "Cebu Wheels & Wander",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "reminders-list",
      sidebarActive: "reminders",
      title: "Follow-ups page",
      hint: "The system nudges the right staff at the right moment: nobody has to remember.",
      data: {
        focusId: "f1",
        items: [
          { id: "f1", priority: "high", status: "pending", type: "Pre-pickup check",
            title: "Confirm pickup logistics with David Cruz",
            message: "Booking picks up in 20h at Mactan Airport. Confirm arrival time and vehicle prep.",
            to: "david@example.com", due: "Today · 16:00" },
          { id: "f2", priority: "medium", status: "pending", type: "Deposit follow-up",
            title: "GCash deposit not yet received: Karen Ong",
            message: "₱2,000 deposit pending. Booking on Jul 21.",
            to: "karen@example.com", due: "Tomorrow · 12:00" },
          { id: "f3", priority: "low", status: "sent", type: "Post-return review invite",
            title: "Send review invite to Maria Santos",
            message: "Booking completed. Ask for a booking-linked review.",
            to: "sam@example.com", due: "Yesterday" },
        ],
      },
      actionLabel: "Send David's follow-up now",
    },
    {
      screen: "banner-info",
      sidebarActive: "reminders",
      data: {
        tone: "ok",
        title: "Follow-up sent: booking activity updated",
        body: "David gets a WhatsApp nudge. The booking's Activity Log records who sent it and when.",
        primary: "Show the booking's activity log",
      },
      actionLabel: "Show activity log",
    },
    {
      screen: "activity-timeline",
      sidebarActive: "activity",
      title: "Booking · David Cruz · Activity Log",
      hint: "Notice the follow-up is now on the booking's timeline, attributed to the exact staff member.",
      data: {
        subject: "David Cruz · Toyota Innova · LFR-4098",
        events: [
          { at: "Today · 16:02", by: "Ana",  role: "Booking Staff",
            what: "Follow-up sent (WhatsApp)", detail: "Pre-pickup logistics confirmation." },
          { at: "Yesterday · 09:30", by: "Rey",  role: "Booking Staff",
            what: "Security deposit ₱2,500 recorded", detail: "GCash reference 202502-EK4A9." },
          { at: "Yesterday · 08:12", by: "Ana",  role: "Booking Staff",
            what: "Booking confirmed by customer", detail: "Confirmation link tapped from Manila IP." },
          { at: "2 days ago",         by: "System", role: "ConfirmPH",
            what: "Draft promoted to Booking",   detail: "Auto-generated confirmation link." },
          { at: "2 days ago",         by: "Ana",  role: "Booking Staff",
            what: "Draft created from WhatsApp inquiry" },
        ],
      },
      outcome: "followed_up",
    },
  ],
  summaries: {
    followed_up: {
      title: "Follow-up landed, and it's on the timeline",
      what: "The reminder found the staff, not the other way around. The booking's Activity Log records exactly who followed up, when, and through which channel.",
      related: ["multi-staff-activity", "upcoming-pickup", "awaiting-confirmation"],
    },
  },
};

const multiStaff = {
  id: "multi-staff-activity",
  kind: "custom",
  business: "Manila Motor Rental",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "bookings-list",
      sidebarActive: "bookings",
      title: "Bookings: open the Hiace booking",
      data: {
        focusId: "b1",
        cta: true,
        bookings: [
          { id: "b1", customer: "Jonas Mendoza", vehicle: "Toyota Hiace · TQP-3311",
            pickup: "Sat · 06:00", ret: "Sun · 22:00", source: "Messenger", status: "Confirmed" },
          { id: "b2", customer: "Cora Delgado", vehicle: "Toyota Vios · MJK-4419",
            pickup: "Mon · 09:00", ret: "Tue · 09:00", source: "Phone", status: "Confirmed" },
        ],
      },
    },
    {
      screen: "booking-detail",
      sidebarActive: "booking-detail",
      title: "Booking · Jonas Mendoza",
      data: {
        booking: {
          id: "TQP-3311", customer: "Jonas Mendoza", vehicle: "Toyota Hiace · TQP-3311",
          pickup: "Sat · 06:00", ret: "Sun · 22:00", source: "Messenger", status: "Confirmed",
          subtotal: "₱9,000", deposit: "₱3,000", balance: "₱6,000",
        },
        primaryAction: "Open full activity log",
      },
      actionLabel: "Open activity log",
    },
    {
      screen: "activity-timeline",
      sidebarActive: "activity",
      title: "Booking · Jonas Mendoza · Full timeline",
      hint: "Owners often ask \"who did what?\": this screen answers that in one place.",
      data: {
        subject: "Jonas Mendoza · Toyota Hiace · TQP-3311",
        events: [
          { at: "Fri · 09:12", by: "Ana",  role: "Booking Staff",     what: "Draft created from Messenger inquiry" },
          { at: "Fri · 09:19", by: "Ana",  role: "Booking Staff",     what: "Security deposit ₱3,000 recorded" },
          { at: "Fri · 10:04", by: "Rey",  role: "Vehicle Checker",   what: "Confirmation link sent (Messenger)" },
          { at: "Fri · 13:47", by: "Rey",  role: "Vehicle Checker",   what: "Customer confirmed via link" },
          { at: "Sat · 05:30", by: "Kim",  role: "Handover Staff",    what: "Vehicle readiness checklist completed" },
          { at: "Sat · 06:02", by: "Kim",  role: "Handover Staff",    what: "Pickup handover: photos & fuel state" },
          { at: "Sat · 06:04", by: "System", role: "ConfirmPH",       what: "Booking marked Ongoing" },
        ],
      },
      outcome: "traced",
    },
  ],
  summaries: {
    traced: {
      title: "Every action attributed to a specific staff member",
      what: "The Hiace booking touched Ana, Rey and Kim. The timeline shows exactly who did what and when: no more \"who sent that confirmation?\" arguments.",
      related: ["employees-management", "staff-availability", "follow-up-reminder"],
    },
  },
};

const upcomingPickup = {
  id: "upcoming-pickup",
  kind: "custom",
  business: "Iloilo Drive PH",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "reminders-list",
      sidebarActive: "reminders",
      title: "Follow-ups page",
      hint: "Pre-pickup reminders keep prep from falling through the cracks.",
      data: {
        focusId: "u1",
        items: [
          { id: "u1", priority: "high", status: "pending", type: "Pre-pickup check",
            title: "Prep Toyota Wigo · TDN-2298 for Faye's pickup",
            message: "Tomorrow · 09:00 pickup. Clean, fuel, run vehicle-readiness checklist.",
            to: "shop@example.com", due: "Tomorrow · 07:30" },
        ],
      },
      actionLabel: "Open the vehicle readiness checklist",
    },
    {
      screen: "checklists-today",
      sidebarActive: "checklists",
      title: "Daily Checklists: Today",
      hint: "Vehicle readiness is one of the built-in checklist types.",
      data: {
        focus: "vehicle_readiness",
        groups: [
          { type: "opening",           typeLabel: "Opening",           name: "Opening: shop", itemCount: 6 },
          { type: "vehicle_readiness", typeLabel: "Vehicle readiness", name: "Vehicle Readiness: Toyota Wigo TDN-2298", itemCount: 8 },
          { type: "cleaning",          typeLabel: "Cleaning",          name: "Vehicle interior cleaning", itemCount: 5 },
          { type: "handover",          typeLabel: "Handover",          name: "Pickup handover", itemCount: 4 },
        ],
      },
      actionLabel: "Start new run",
    },
    {
      screen: "checklist-run",
      sidebarActive: "checklist-run",
      title: "Vehicle readiness: Toyota Wigo",
      hint: "Every required item must be ticked before submission.",
      data: {
        name: "Vehicle Readiness: Toyota Wigo TDN-2298",
        type: "Vehicle readiness", vehicle: "Toyota Wigo · TDN-2298",
        items: [
          { label: "Exterior clean, no visible damage",   required: true,  checked: true  },
          { label: "Interior vacuumed, seats wiped",        required: true,  checked: true  },
          { label: "Fuel tank ≥ ¾",                          required: true,  checked: true  },
          { label: "Tyres inflated (incl. spare)",           required: true,  checked: true  },
          { label: "Tools + jack + first-aid present",       required: true,  checked: true  },
          { label: "OR/CR + insurance placed in glovebox",   required: true,  checked: true  },
          { label: "Air-freshener + charger present",        required: false, checked: false, note: "Optional" },
          { label: "Key handover photo taken",               required: true,  checked: true  },
        ],
      },
      actionLabel: "Submit checklist",
    },
    {
      screen: "banner-info",
      sidebarActive: "checklists",
      data: {
        tone: "ok",
        title: "Checklist submitted: booking prep confirmed",
        body: "Timestamped and attributed to the staff member. Faye's pickup is officially ready.",
      },
      outcome: "prep_done",
    },
  ],
  summaries: {
    prep_done: {
      title: "Pickup prepped with a paper-trail",
      what: "The Vehicle Readiness checklist was completed against Faye's exact booking. Every tick is timestamped and attributed to a specific staff account.",
      related: ["daily-checklists", "follow-up-reminder", "multi-staff-activity"],
    },
  },
};

// -----------------------------------------------------------------
// VISIBILITY & TRUST
// -----------------------------------------------------------------

const verifiedReview = {
  id: "verified-review",
  kind: "custom",
  business: "Dumaguete Island Wheels",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "bookings-list",
      sidebarActive: "bookings",
      title: "Completed booking: Maria Santos",
      data: {
        focusId: "b1",
        cta: true,
        bookings: [
          { id: "b1", customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
            pickup: "Last week", ret: "This week", source: "Messenger", status: "Completed" },
          { id: "b2", customer: "Michael Thompson", vehicle: "Honda City · JAX-8842",
            pickup: "Next week", ret: "Next week", source: "Phone", status: "Confirmed" },
        ],
      },
    },
    {
      screen: "booking-detail",
      sidebarActive: "booking-detail",
      title: "Booking · Maria Santos",
      hint: "Only customers with a completed ConfirmPH booking can leave a booking-linked review.",
      data: {
        booking: {
          id: "WSJ-2211-MS", customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
          pickup: "Last week", ret: "This week", source: "Messenger", status: "Completed",
          subtotal: "₱3,400", deposit: "₱1,000", balance: "₱0",
        },
        primaryAction: "Send review invite",
      },
      actionLabel: "Send review invite",
    },
    {
      screen: "review-invite",
      sidebarActive: "review-invite",
      title: "Review invite",
      data: {
        customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
        channel: "Messenger: same thread as the confirmation",
      },
      actionLabel: "Send invite",
    },
    {
      screen: "review-public",
      sidebarActive: "review-public",
      title: "Booking-linked review live on your public profile",
      data: {
        author: "Maria Santos", stars: 5, when: "Today",
        vehicle: "Toyota Vios · Confirmed booking",
        body: "Booking flow was smooth. Vehicle was clean and the Vios ran perfectly around Dumaguete. Highly recommended.",
      },
      outcome: "review_live",
    },
  ],
  summaries: {
    review_live: {
      title: "Booking-linked review boosted your public profile",
      what: "Only booking-completed customers can leave a review: that's why it's marked booking-linked. The same review appears on your Business Page and your ConfirmPH Website.",
      related: ["business-page", "public-website", "messenger-inquiry"],
    },
  },
};

// -----------------------------------------------------------------
// NEW: OWNER / OPERATIONS
// -----------------------------------------------------------------

const employeesManagement = {
  id: "employees-management",
  kind: "custom",
  business: "Cebu Wheels & Wander",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "employees-list",
      sidebarActive: "employees",
      title: "Employees: owner-only",
      hint: "Only the business owner can see this screen. It's how you delegate ConfirmPH access.",
      data: {
        employees: [
          { id: "e1", name: "Ana Reyes",    title: "Booking Staff",    login: "ana@cebuwheels.ph",   active: true,  permissions: ["bookings", "reminders"] },
          { id: "e2", name: "Rey Bautista", title: "Vehicle Checker",  login: "rey@cebuwheels.ph",   active: true,  permissions: ["vehicles", "incidents"] },
          { id: "e3", name: "Kim Salvador", title: "Handover Staff",   login: "kim@cebuwheels.ph",   active: true,  permissions: ["bookings"] },
          { id: "e4", name: "Boy Delgado",  title: "Weekend Driver",   login: "boy@cebuwheels.ph",   active: false, permissions: ["bookings"] },
        ],
        ctaLabel: "Add employee",
      },
      actionLabel: "Open Add Employee form",
    },
    {
      screen: "employee-form",
      sidebarActive: "employee-form",
      title: "New employee: Gina",
      hint: "Only tick the permissions Gina actually needs. She won't see anything else.",
      data: {
        name: "Gina Ocampo", title: "Booking Staff",
        email: "gina@cebuwheels.ph",
        permissions: ["bookings", "reminders"],
      },
      actionLabel: "Create employee",
    },
    {
      screen: "employees-list",
      sidebarActive: "employees",
      title: "Gina added: permissions applied",
      hint: "She can log in immediately with the password you set.",
      data: {
        focusId: "e5",
        employees: [
          { id: "e5", name: "Gina Ocampo",  title: "Booking Staff",   login: "gina@cebuwheels.ph", active: true,  permissions: ["bookings", "reminders"] },
          { id: "e1", name: "Ana Reyes",    title: "Booking Staff",   login: "ana@cebuwheels.ph",  active: true,  permissions: ["bookings", "reminders"] },
          { id: "e2", name: "Rey Bautista", title: "Vehicle Checker", login: "rey@cebuwheels.ph",  active: true,  permissions: ["vehicles", "incidents"] },
          { id: "e3", name: "Kim Salvador", title: "Handover Staff",  login: "kim@cebuwheels.ph",  active: true,  permissions: ["bookings"] },
        ],
      },
      actionLabel: "Open Gina's Work Summary",
    },
    {
      screen: "employee-performance",
      sidebarActive: "employee-performance",
      title: "Work Summary: Gina Ocampo (day 1)",
      hint: "Privacy-friendly aggregates only. No keystroke tracking, no per-action timestamps.",
      data: {
        name: "Gina Ocampo", login: "gina@cebuwheels.ph",
        metrics: { bookings_created: 3, bookings_completed: 0, bookings_cancelled: 0,
                   incidents_resolved: 0, verification_requests_handled: 1, vehicles_added: 0 },
      },
      outcome: "employee_onboarded",
    },
  ],
  summaries: {
    employee_onboarded: {
      title: "New staff onboarded with the right permissions",
      what: "Gina got exactly the access she needs: bookings + follow-ups, and nothing else. Every action she takes is now attributable to her account.",
      related: ["staff-availability", "multi-staff-activity", "follow-up-reminder"],
    },
  },
};

const staffAvailability = {
  id: "staff-availability",
  kind: "custom",
  business: "Iloilo Drive PH",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "staff-availability",
      sidebarActive: "staff-availability",
      title: "Who's on the clock",
      hint: "Inquiries and bookings route to a clocked-in staff member with the right permissions. If nobody's on, the owner is the fallback.",
      data: {
        online: [
          { id: "s1", name: "Ana Reyes",    title: "Booking Staff",  status: "available", clockedFor: "2h 15m ago" },
          { id: "s2", name: "Rey Bautista", title: "Vehicle Checker", status: "busy",     clockedFor: "45m ago" },
        ],
        offline: [
          { id: "s3", name: "Kim Salvador", title: "Handover Staff", lastSeen: "Yesterday · 22:04" },
        ],
        history: [
          { name: "Ana Reyes",    in: "Today 07:30",     out: "",              duration: "2h 15m" },
          { name: "Rey Bautista", in: "Today 09:00",     out: "",              duration: "45m" },
          { name: "Kim Salvador", in: "Yesterday 14:00", out: "Yesterday 22:04", duration: "8h 4m" },
          { name: "Ana Reyes",    in: "Yesterday 07:15", out: "Yesterday 18:20", duration: "11h 5m" },
        ],
      },
    },
    {
      screen: "banner-info",
      sidebarActive: "staff-availability",
      data: {
        tone: "ok",
        title: "New WhatsApp inquiry auto-routes to Ana",
        body: "Ana is clocked in with 'Bookings' permission. Rey is 'busy' and Kim is offline, so the inquiry lands on Ana's dashboard immediately.",
        primary: "Open the booking Ana just picked up",
      },
    },
    {
      screen: "activity-timeline",
      sidebarActive: "activity",
      title: "Booking activity: attributed to Ana",
      hint: "The clock-in/out audit and per-booking activity together make every action fully traceable.",
      data: {
        subject: "New WhatsApp inquiry · David Cruz · Innova LFR-4098",
        events: [
          { at: "Today · 09:47", by: "Ana",  role: "Booking Staff",
            what: "Draft created from WhatsApp inquiry", detail: "Routed via clock-in rules (Ana online, Rey busy)." },
          { at: "Today · 09:46", by: "System", role: "ConfirmPH",
            what: "Inquiry received (WhatsApp)" },
          { at: "Today · 07:30", by: "Ana",  role: "Booking Staff",
            what: "Clocked in", detail: "Marked status: available." },
        ],
      },
      outcome: "shift_routed",
    },
  ],
  summaries: {
    shift_routed: {
      title: "Right staff, right booking, no hand-off delays",
      what: "The clock-in state, permissions and 'available/busy' pill together decide where the inquiry goes. Every step, including the clock-in itself: is on the audit trail.",
      related: ["employees-management", "multi-staff-activity", "follow-up-reminder"],
    },
  },
};

const vehiclesManagement = {
  id: "vehicles-management",
  kind: "custom",
  business: "Bacolod Ride Hub",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "vehicles-list",
      sidebarActive: "vehicles",
      title: "Vehicles page",
      data: {
        fleet: { plan: "Starter", used: 6, total: 10 },
        vehicles: [
          { id: "v1", name: "Toyota Vios",    plate: "WSJ-2211", status: "available", category: "Sedan",     transmission: "Automatic", seats: 5, price: 1700 },
          { id: "v2", name: "Toyota Innova",  plate: "LFR-4098", status: "booked",    category: "Van",       transmission: "Automatic", seats: 7, price: 2800 },
          { id: "v3", name: "Yamaha NMax",    plate: "155A-XY",  status: "available", category: "Scooter",   transmission: "Automatic", seats: 2, price: 650  },
        ],
        ctaLabel: "Add vehicle",
      },
      actionLabel: "Open Add Vehicle form",
    },
    {
      screen: "vehicle-form",
      sidebarActive: "vehicle-form",
      title: "New vehicle: Honda BR-V",
      hint: "The Add Vehicle modal is tabbed: Basic Info, Technical, Pricing, Public, Rules, Internal, Visibility. Fill Basic first.",
      data: {
        name: "Honda BR-V", plate: "KCM-6641", category: "SUV",
        make: "Honda", model: "BR-V", price: "2200", deposit: "1500",
      },
      actionLabel: "Save vehicle",
    },
    {
      screen: "vehicles-list",
      sidebarActive: "vehicles",
      title: "Honda BR-V added to the fleet",
      hint: "Fleet points bumped. Vehicle is now bookable from Notepad, dashboard and public pages.",
      data: {
        fleet: { plan: "Starter", used: 9, total: 10 },
        vehicles: [
          { id: "v4", name: "Honda BR-V",    plate: "KCM-6641", status: "available", category: "SUV",      transmission: "Automatic", seats: 7, price: 2200 },
          { id: "v1", name: "Toyota Vios",    plate: "WSJ-2211", status: "available", category: "Sedan",    transmission: "Automatic", seats: 5, price: 1700 },
          { id: "v2", name: "Toyota Innova",  plate: "LFR-4098", status: "booked",    category: "Van",      transmission: "Automatic", seats: 7, price: 2800 },
          { id: "v3", name: "Yamaha NMax",    plate: "155A-XY",  status: "available", category: "Scooter",  transmission: "Automatic", seats: 2, price: 650  },
        ],
      },
      outcome: "vehicle_added",
    },
  ],
  summaries: {
    vehicle_added: {
      title: "Fleet updated in under a minute",
      what: "The Honda BR-V is now bookable everywhere: Notepad, Business Page, Website, and the Booking Intake. Public listing and pricing are controlled per-vehicle from the same form.",
      related: ["business-page", "vehicle-breakdown", "maintenance"],
    },
  },
};

const dailyChecklists = {
  id: "daily-checklists",
  kind: "custom",
  business: "Cebu Wheels & Wander",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "checklists-today",
      sidebarActive: "checklists",
      title: "Daily Checklists: Today",
      hint: "Opening, closing, vehicle readiness, cleaning, handover: all pre-seeded and editable per business.",
      data: {
        focus: "opening",
        groups: [
          { type: "opening",           typeLabel: "Opening",            name: "Opening: shop",             itemCount: 6 },
          { type: "closing",           typeLabel: "Closing",            name: "Closing: shop",             itemCount: 5 },
          { type: "vehicle_readiness", typeLabel: "Vehicle readiness",  name: "Vehicle Readiness",          itemCount: 8 },
          { type: "cleaning",          typeLabel: "Cleaning",           name: "Interior cleaning",           itemCount: 5 },
          { type: "handover",          typeLabel: "Handover",           name: "Pickup handover",             itemCount: 4 },
          { type: "fuel_charge",       typeLabel: "Fuel / charge",      name: "Fuel / charge check",         itemCount: 3 },
        ],
      },
      actionLabel: "Start Opening checklist",
    },
    {
      screen: "checklist-run",
      sidebarActive: "checklist-run",
      title: "Opening: shop",
      data: {
        name: "Opening: shop", type: "Opening", vehicle: "Shop counter",
        items: [
          { label: "Reception lights on",             required: true,  checked: true  },
          { label: "Cash float present + reconciled", required: true,  checked: true  },
          { label: "Sign / branding placed outside",  required: true,  checked: true  },
          { label: "Yesterday's overnight returns logged", required: true, checked: true },
          { label: "Key board fully stocked",         required: true,  checked: true  },
          { label: "Water / coffee for customers",    required: false, checked: false, note: "Optional" },
        ],
      },
      actionLabel: "Submit opening checklist",
    },
    {
      screen: "banner-info",
      sidebarActive: "checklists",
      data: {
        tone: "ok",
        title: "Opening checklist done: timestamped and attributed",
        body: "Owner can see who opened at what time. Missed opening days show a red banner the next morning.",
      },
      outcome: "checklist_done",
    },
  ],
  summaries: {
    checklist_done: {
      title: "Daily routine locked into a paper-trail",
      what: "Opening, closing, and handover run through the same Daily Checklists page. Missed days surface a red banner next morning so nothing quietly gets skipped.",
      related: ["upcoming-pickup", "multi-staff-activity", "staff-availability"],
    },
  },
};

const warningsCompliance = {
  id: "warnings-compliance",
  kind: "custom",
  business: "Manila Motor Rental",
  supportsDeviceToggle: false,
  steps: [
    {
      screen: "warnings-list",
      sidebarActive: "warnings",
      title: "Warning Center: active operational risks",
      hint: "Owners often ask \"what's currently going wrong?\": this screen answers that in one place.",
      data: {
        focusId: "w1",
        items: [
          { id: "w1", severity: "critical", status: "active", type: "Return overdue",
            title: "Rafael Uy · Suzuki Ertiga overdue 2h30m",
            message: "Next booking begins at 12:00. Overdue return risk on MOP-9911.",
            recommended: "Message Rafael + prepare replacement for Zara at 12:00.",
            primary: "Reassign next booking's vehicle" },
          { id: "w2", severity: "high", status: "active", type: "Deposit unresolved",
            title: "Karen Ong · ₱2,000 GCash deposit not received",
            message: "Booking on Jul 21. Deposit hasn't landed 24h after booking.",
            recommended: "Send GCash reminder or convert to cash-on-pickup." },
          { id: "w3", severity: "medium", status: "active", type: "Inspection missing",
            title: "Ford EcoSport · returned yesterday, no inspection",
            message: "Vehicle back in the yard but return inspection not logged.",
            recommended: "Complete the return inspection or block the vehicle." },
        ],
      },
      actionLabel: "Reassign Zara's booking to another van",
    },
    {
      screen: "banner-info",
      sidebarActive: "warnings",
      data: {
        tone: "ok",
        title: "Zara reassigned to Toyota Grandia · GRN-7712",
        body: "Warning auto-resolves the moment the affected booking has a working vehicle. Zara stays confirmed.",
      },
      outcome: "warning_resolved",
    },
  ],
  summaries: {
    warning_resolved: {
      title: "Owner cockpit for operational risk",
      what: "Late returns, unpaid deposits, missing inspections, and buffer violations appear together with a real primary action for each item.",
      related: ["late-return", "vehicle-breakdown", "damage-incident"],
    },
  },
};

// -----------------------------------------------------------------
// RENTAL HANDOVER / RETURN: production components, local demo adapter
// -----------------------------------------------------------------
const pickupHandoverLive = {
  id: "pickup-handover",
  kind: "custom",
  business: "Dumaguete Island Wheels",
  defaultDevice: "phone",
  steps: [{
    screen: "pickup-handover-live",
    sidebarActive: "bookings",
    title: "Confirmed booking · pickup handover",
    data: {
      handover: {
        id: "demo-handover-2211", status: "in_progress", item_id: "demo-vios-2211",
        existing_damage_reviewed: false,
        existing_damage: [{ id: "damage-1", tag_code: "passenger_door_scuff", view: "right", severity: "minor", notes: "Existing scuff photographed on previous return." }],
        keys_items_released: [
          { item: "Primary key", released: false },
          { item: "Spare key", released: false },
          { item: "Registration / OR-CR", released: false },
        ],
        internal_notes: "",
        inspection: {
          id: "demo-pickup-inspection", status: "draft", odometer_km: 58120, fuel_level: 100,
          customer_signature_name: "Maria Santos", customer_acknowledged: true, photo_count: 0,
        },
        photos: [],
      },
    },
    outcome: "ongoing",
  }],
  summaries: { ongoing: { title: "Pickup handover completed", what: "The real handover captures mileage, fuel, prior damage, photos, released keys and acknowledgement before the canonical service moves the booking to Ongoing." } },
};

const returnAndReadyLive = {
  id: "return-and-ready",
  kind: "custom",
  business: "Dumaguete Island Wheels",
  defaultDevice: "phone",
  steps: [
    {
      screen: "reminders-list",
      sidebarActive: "reminders",
      title: "Follow-ups page",
      hint: "The same return-due reminder used throughout the app: nothing new to learn.",
      data: {
        focusId: "r1",
        items: [
          { id: "r1", priority: "high", status: "pending", type: "Return due",
            title: "Toyota Vios · WSJ-2211 return expected today",
            message: "Maria Santos · Aug 20 · 09:00. Prepare for vehicle check-in and inspection.",
            to: "shop@example.com", due: "Today · 09:00" },
        ],
      },
      actionLabel: "Open Maria's booking",
    },
    {
      screen: "return-readiness-live", sidebarActive: "bookings", title: "Open the ongoing booking and record its return",
      data: { booking: { id: "DEMO-RET-2211", status: "ongoing", vehicle: { id: "demo-vios-2211", name: "Toyota Vios", status: "in_use" } } },
    },
    {
      screen: "return-readiness-live", sidebarActive: "bookings", title: "Vehicle is automatically held for inspection",
      data: { booking: { id: "DEMO-RET-2211", status: "completed", vehicle: { id: "demo-vios-2211", name: "Toyota Vios", status: "returned_needs_check" } } },
    },
    {
      screen: "return-inspection-live", sidebarActive: "bookings", title: "Complete the real return inspection",
      data: {
        inspection: {
          id: "demo-return-inspection", status: "draft", inspection_type: "return",
          fuel_level: 100, odometer_km: 58410, cleaning_condition: "clean",
          exterior_notes: "Existing passenger-door scuff unchanged; no new damage.", interior_notes: "Clean and complete.",
          summary_note: "Returned on time. Existing condition matches pickup record.",
          customer_signature_name: "Maria Santos", customer_acknowledged: true, photo_count: 0,
          checklist: [
            { key: "exterior_body", label: "Exterior body", rating: "ok", note: "Existing documented scuff unchanged" },
            { key: "tyres", label: "Tyres", rating: "ok", note: "" },
            { key: "interior_cabin", label: "Interior cabin", rating: "ok", note: "" },
            { key: "documents", label: "Documents / OR-CR", rating: "ok", note: "" },
          ],
        },
        photos: [],
        template: { id: "demo-return-template", name: "Standard return check" },
      },
    },
    {
      screen: "return-readiness-live", sidebarActive: "bookings", title: "Inspection clears the vehicle for its assigned next booking",
      data: {
        booking: { id: "DEMO-RET-2211", status: "completed", vehicle: { id: "demo-vios-2211", name: "Toyota Vios", status: "available" } },
        returnInspection: { id: "demo-return-inspection", status: "finalized" },
        nextBooking: { id: "DEMO-NEXT-2211", booking_reference: "DIW-2284", start_datetime: "2026-08-25T09:00:00+08:00", status: "confirmed" },
      },
      outcome: "ready",
    },
  ],
  summaries: { ready: { title: "Return closed cleanly", what: "Completion put the vehicle in Needs Inspection. The finalized return check updated mileage and cleared it to Available; its existing next booking stayed assigned throughout." } },
};

const incident = INCIDENT_SCENARIO;
const incidentBooking = {
  id: incident.booking.id, booking_reference: incident.booking.reference,
  customer_name: incident.affectedRenter.name, customer_email: incident.affectedRenter.email,
  customer_phone: incident.affectedRenter.phone, status: "confirmed", risk_status: "affected_action_needed",
  start_datetime: "2026-09-04T09:00:00+08:00", end_datetime: "2026-09-06T18:00:00+08:00",
  price: 6400, deposit_required: 2000, pickup_location: "Traveler Car Rental office",
  return_location: "Traveler Car Rental office", reconfirmation_required: false,
};
const incidentWarning = {
  id: "incident-warning-vb-1", warning_type: "vehicle_unavailable", severity: "critical", status: "active",
  title: "Upcoming confirmed booking affected",
  message: "Kia Rio · PYR-5502 is unavailable and assigned to TCR-4821 for Anna Reyes tomorrow at 09:00.",
  recommended_action: "Notify the renter and reassign a suitable available vehicle, or cancel/change the booking if none is available.",
  related_booking_id: incident.booking.id, related_vehicle_id: incident.damagedVehicle.id,
  detected_at: "2026-09-03T09:15:00+08:00", last_detected_at: "2026-09-03T09:15:00+08:00",
};
const incidentReplacement = {
  booking: {
    booking_reference: incident.booking.reference, customer_name: incident.affectedRenter.name,
    current_vehicle: { name: incident.damagedVehicle.name, plate_number: incident.damagedVehicle.plate_number },
    start_datetime: incidentBooking.start_datetime, end_datetime: incidentBooking.end_datetime,
  },
  candidates: [{
    ...incident.replacementVehicle, availability: "available", availability_label: "Available",
    category_match: false, vehicle_warnings: [], conflicts: { has_critical: false },
  }],
};
const incidentConfirmation = {
  booking: { ...incidentBooking, status: "awaiting_customer_confirmation", reconfirmation_required: true },
  company: incident.business, vehicle: incident.replacementVehicle, expired: false, token_status: "active", terms: null, damages: [],
  version_diff: {
    changed_fields: ["item_id"],
    previous_confirmed_snapshot: { vehicle_name: "Kia Rio (PYR-5502)" },
    current_snapshot: { vehicle_name: "Toyota Montero (KDQ-7710)" },
  },
};

// The complete incident is deliberately implemented in the existing custom
// workflow engine, but its ConfirmPH beats mount production components below
// (WarningCard, ReassignVehicleModal, CustomerConfirmation, vehicle overview,
// damage map and InspectionSheet). Only the external conversations are bespoke.
const vehicleBreakdownDetailed = {
  id: "vehicle-breakdown", kind: "custom", business: incident.business.name,
  defaultDevice: "desktop", supportsDeviceToggle: true,
  // Scoped ONLY to the homepage-embedded business walkthrough (embed=1: see
  // CustomWorkflow.jsx's step-capping and BusinessProductWalkthrough.jsx's
  // ScenarioFrame). The standalone /demo-business/scenarios/vehicle-breakdown
  // page (no embed param) always renders the full steps array below,
  // unchanged, including the deeper workshop/repair/inspection/return tail.
  embedStopAtScreen: "business-resolution-live",
  steps: [
    { screen: "incident-chat-live", sidebarActive: "dashboard", title: "Accident message arrives", guidance: ["Open the accident message.", "The conversation is outside ConfirmPH; the photo and report trigger the staff workflow."], data: { messages: INCIDENT_CHAT.accident, kind: "accident" } },
    { screen: "vehicle-command-live", sidebarActive: "vehicles", title: "Open the affected vehicle", guidance: ["Open Kia Rio's vehicle details, then Log incident.", "The real vehicle overview shows its next booking and recorded operational state, before anything has been logged yet."], data: { status: "Available", action: "Log incident", mode: "incident" } },
    // The real product records an incident's type and severity on one form
    // (IncidentNew.jsx: incident_type + severity together, with an
    // "update vehicle status" option that defaults on) - so this screen
    // walks the SAME real taxonomy (lib/incidents.jsx's INCIDENT_TYPE_META /
    // SEVERITY_META) as two selectable phases inside one step, rather than
    // inventing a second parallel incident-entry system. See
    // IncidentLogLiveScreen in CustomWorkflow.jsx.
    { screen: "incident-log-live", sidebarActive: "vehicles", title: "Report the incident", guidance: ["Select Accident, then severity Critical.", "Same real incident type and severity taxonomy used across ConfirmPH: nothing invented for this demo."], data: {} },
    { screen: "vehicle-command-live", sidebarActive: "vehicles", title: "Vehicle status updates", guidance: ["Review the updated vehicle status.", "Accident -> Damaged is the same canonical mapping the real incident system applies automatically."], data: { status: "Damaged", action: "Continue", mode: "status-updated", incidentType: "accident", severity: "critical" } },
    { screen: "warning-live", sidebarActive: "warnings", title: "Upcoming booking at risk", guidance: ["Notify the affected renter.", "Only now - after the incident is recorded and the vehicle is Damaged - does ConfirmPH surface the affected-booking warning."], data: { mode: "notify" } },
    { screen: "incident-chat-live", sidebarActive: "warnings", title: "Anna contacts the business", guidance: ["Read Anna's message, then continue.", "Staff explains that the vehicle is unavailable without promising a replacement."], data: { messages: INCIDENT_CHAT.affectedRenter, kind: "affected" } },
    { screen: "warning-reassign-live", sidebarActive: "warnings", title: "Open the warning and change the booking", guidance: ["Choose the replacement vehicle.", "Toyota Montero is the deterministic candidate with no critical conflict. The secondary branch handles no suitable replacement."], data: {} },
    { screen: "reconfirmation-live", sidebarActive: "bookings", title: "Send the updated confirmation", guidance: ["Send the updated confirmation.", "A vehicle change is material, so Anna must explicitly reconfirm."], data: {} },
    { screen: "customer-confirmation-live", sidebarActive: "bookings", title: "Anna reviews and accepts", guidance: ["Confirm the changes.", "This is the same production reconfirmation component the renter walkthrough uses."], data: {} },
    { screen: "business-resolution-live", sidebarActive: "bookings", title: "The business sees the resolution", guidance: ["Open the resolved booking record.", "The replacement, renter acceptance, and cleared warning remain visible in the history."], data: {} },
    { screen: "incident-chat-live", sidebarActive: "vehicles", title: "Workshop update", guidance: ["Open the workshop message.", "The repair is complete and cleared for use, but remaining cosmetic damage must stay recorded."], data: { messages: INCIDENT_CHAT.recovery, kind: "recovery" } },
    { screen: "damage-record-live", sidebarActive: "vehicles", title: "Preserve the remaining damage", guidance: ["Record the remaining vehicle damage.", "Returning to service does not erase incident or damage history."], data: { stage: "remaining" } },
    { screen: "return-inspection-live", sidebarActive: "vehicles", title: "Complete the readiness inspection", guidance: ["Finalize the inspection.", "The vehicle cannot return to Available until the real inspection gate is complete."], data: { inspection: { id: "incident-clearance", status: "draft", inspection_type: "return", fuel_level: 75, odometer_km: 62310, cleaning_condition: "clean", exterior_notes: incident.remainingDamageNote, interior_notes: "Cleared", summary_note: "Repair completed; remaining cosmetic damage documented.", customer_acknowledged: true, customer_signature_name: "Traveler Car Rental workshop clearance", checklist: [{ key: "exterior_body", label: "Exterior body", rating: "needs_attention", note: incident.remainingDamageNote }, { key: "tyres", label: "Tyres", rating: "ok", note: "" }, { key: "interior_cabin", label: "Interior cabin", rating: "ok", note: "" }, { key: "documents", label: "Documents / OR-CR", rating: "ok", note: "" }] }, photos: [], template: { id: "incident-clearance-template", name: "Vehicle clearance check" } } },
    { screen: "vehicle-command-live", sidebarActive: "vehicles", title: "Return the vehicle to service", guidance: ["Mark the vehicle available after it is cleared.", "The incident and remaining damage stay in history while the cleared vehicle returns to service."], data: { status: "Damaged", action: "Mark Available", mode: "recovery" }, outcome: "resolved" },
  ],
  summaries: {
    resolved: { title: "Incident resolved with a complete record", what: "The affected renter approved the replacement. Kia Rio returned only after repair and inspection, while its incident and remaining cosmetic damage stayed on record." },
    cancelled: { title: "Booking cancelled without false promises", what: "No suitable replacement was available, so the business cancelled the booking and sent Anna a customer-safe status update. Payment, refund, or other arrangements remain between Anna and the rental business." },
  },
};

// -----------------------------------------------------------------
// EXPORT
// -----------------------------------------------------------------
const customWorkflows = {
  [vehicleBreakdownDetailed.id]: vehicleBreakdownDetailed,
  [pickupHandoverLive.id]:    pickupHandoverLive,
  [returnAndReadyLive.id]:    returnAndReadyLive,
  [awaitingConfirmation.id]:  awaitingConfirmation,
  [forgottenBooking.id]:      forgottenBooking,
  [dateChange.id]:            dateChange,
  [maintenance.id]:           maintenance,
  [damageIncident.id]:        damageIncident,
  [followUp.id]:              followUp,
  [multiStaff.id]:            multiStaff,
  [upcomingPickup.id]:        upcomingPickup,
  [verifiedReview.id]:        verifiedReview,
  [employeesManagement.id]:   employeesManagement,
  [staffAvailability.id]:     staffAvailability,
  [vehiclesManagement.id]:    vehiclesManagement,
  [dailyChecklists.id]:       dailyChecklists,
  [warningsCompliance.id]:    warningsCompliance,
};
export default customWorkflows;
